import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Loader2, Send } from "lucide-react";

const NewCampaignPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    description: "",
    status: "bozza",
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post("/campaigns", data),
    onSuccess: (response) => {
      toast.success("Campagna creata con successo!");
      navigate(`/campaigns/${response.data.id}`);
    },
    onError: () => {
      toast.error("Errore durante la creazione della campagna");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.subject.trim()) {
      toast.error("Nome e oggetto sono obbligatori");
      return;
    }
    createMutation.mutate(formData);
  };

  return (
    <div className="space-y-8" data-testid="new-campaign-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/campaigns")}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading">Nuova Campagna</h1>
          <p className="text-muted-foreground">Crea una nuova campagna di email tracking</p>
        </div>
      </div>

      {/* Form */}
      <Card className="max-w-2xl bg-white border border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Dettagli Campagna
          </CardTitle>
          <CardDescription>
            Inserisci le informazioni della tua campagna email
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nome campagna *</Label>
                <Input
                  id="name"
                  placeholder="es. Newsletter Gennaio 2024"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="campaign-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Stato</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="campaign-status-select">
                    <SelectValue placeholder="Seleziona stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bozza">Bozza</SelectItem>
                    <SelectItem value="attiva">Attiva</SelectItem>
                    <SelectItem value="archiviata">Archiviata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Oggetto email *</Label>
              <Input
                id="subject"
                placeholder="es. Le novità di questo mese..."
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                data-testid="campaign-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione (opzionale)</Label>
              <Textarea
                id="description"
                placeholder="Aggiungi una descrizione per identificare facilmente questa campagna..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                data-testid="campaign-description-input"
              />
            </div>

            <div className="flex items-center gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/campaigns")}
                data-testid="cancel-btn"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-primary hover:bg-primary-hover"
                disabled={createMutation.isPending}
                data-testid="submit-campaign-btn"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  "Crea Campagna"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewCampaignPage;
